import { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { getExpressAccounts } from "../services/expressMaster";
import {
  ExpressAccount,
  ExpressMasterList,
  ExpressMasterListParams,
} from "../types/express-master.type";
import { useDebounce } from "../utils/hooks/useDebounce";
import MasterFilter, {
  EMPTY_MASTER_FILTER,
  MasterFilterValue,
} from "../components/ExpressMaster/MasterFilter";
import MasterTable, { MasterColumn } from "../components/ExpressMaster/MasterTable";
import AccountDialog from "../components/ExpressMaster/AccountDialog";
import { useMasterList } from "../components/ExpressMaster/useMasterList";
import { EXPRESS_ACCOUNT_MENU_TITLE, isActiveParam } from "../components/ExpressMaster/constants";

const SEARCH_DEBOUNCE_MS = 500;

const fetchAccounts = async (query: ExpressMasterListParams): Promise<ExpressMasterList<ExpressAccount>> => {
  const {
    data: { data },
  } = await getExpressAccounts(query);
  return data ?? { rows: [], count: 0 };
};

const ACCOUNT_COLUMNS: MasterColumn<ExpressAccount>[] = [
  { key: "code", label: "รหัส", render: (account) => account.code },
  { key: "name", label: "ชื่อบัญชี", render: (account) => account.name },
];

/** รหัสบัญชี (Express) — head office keeps the account codes the document lines book to. */
export default function ExpressAccountPage() {
  const [filter, setFilter] = useState<MasterFilterValue>(EMPTY_MASTER_FILTER);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [editingAccount, setEditingAccount] = useState<ExpressAccount | null>(null);
  const debouncedSearch = useDebounce(filter.search, SEARCH_DEBOUNCE_MS);
  const { status } = filter;
  const filters = useMemo(
    () => ({ search: debouncedSearch.trim() || undefined, is_active: isActiveParam(status) }),
    [debouncedSearch, status],
  );
  const list = useMasterList(fetchAccounts, filters);
  const { reload } = list;

  const handleFilterChange = (patch: Partial<MasterFilterValue>) =>
    setFilter((previous) => ({ ...previous, ...patch }));
  const handleAdd = () => {
    setEditingAccount(null);
    setIsDialogOpen(true);
  };
  const handleEdit = (account: ExpressAccount) => {
    setEditingAccount(account);
    setIsDialogOpen(true);
  };
  const handleCloseDialog = () => setIsDialogOpen(false);
  const handleSaved = () => {
    setIsDialogOpen(false);
    reload();
  };

  return (
    <Stack sx={{ padding: "8px 0" }} gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Typography variant="h6" fontWeight={700}>
          {EXPRESS_ACCOUNT_MENU_TITLE}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          เพิ่มรหัสบัญชี
        </Button>
      </Stack>
      <Paper sx={{ padding: "24px 16px" }}>
        <MasterFilter value={filter} hasKind={false} onChange={handleFilterChange} />
      </Paper>
      <MasterTable columns={ACCOUNT_COLUMNS} list={list} onEdit={handleEdit} />
      <AccountDialog
        open={isDialogOpen}
        account={editingAccount}
        onClose={handleCloseDialog}
        onSaved={handleSaved}
      />
    </Stack>
  );
}
